import { ApolloCache } from '@apollo/client';

import {
  GetTaskDocument,
  Task,
  TaskMemberRole,
  User,
  useDeleteTaskInvitationMutation,
  useDeleteUserRequestMutation,
  useResendTaskInvitationMutation,
  useUpdateTaskMembersMutation,
  useUpdateTaskMutation,
} from '@graphql/generated';
import { getTaskRoleEnumValue } from '@utils/tasks';

import type { UserSearchType } from '@components/TaskList/TaskInviteOrAssignee.web';

interface UseTaskMemberMutationsParams {
  taskId: string;
  task: Task | Record<string, any>;
}

interface RemoveMemberCallbacks {
  onError: () => void;
  onCompleted: () => void;
}

interface RemoveInviteCallbacks {
  onError: () => void;
  onCompleted: () => void;
}

interface ResendInviteCallbacks {
  onError: () => void;
  onCompleted: () => void;
}

interface UpdateRoleCallbacks {
  onError: () => void;
  onCompleted: () => void;
}

/**
 * Removes an invitation ref from the Apollo cache for a given task.
 */
export const removeInvitationFromCache = (
  cache: ApolloCache<unknown>,
  taskTypename: string,
  taskId: string,
  invitationId: string
) => {
  cache.modify({
    id: cache.identify({ __typename: taskTypename, id: taskId }),
    fields: {
      invitations(existingRefs = [], { readField }) {
        return (existingRefs as any[]).filter(
          (ref) => readField('id', ref) !== invitationId
        );
      },
    },
  });
};

/**
 * Removes a user request ref from the Apollo cache for a given task.
 * This function modifies the `userRequests` field in the cache for a given task.
 */
export const removeUserRequestFromCache = (
  cache: ApolloCache<unknown>,
  taskTypename: string,
  taskId: string,
  userRequestId: string
) => {
  cache.modify({
    id: cache.identify({ __typename: taskTypename, id: taskId }),
    fields: {
      userRequests(existingRefs = [], { readField }) {
        return (existingRefs as any[]).filter(
          (ref) => readField('id', ref) !== userRequestId
        );
      },
    },
  });
};

export const useTaskMemberMutations = ({
  taskId,
  task,
}: UseTaskMemberMutationsParams) => {
  const { id, name, projectId, members = [] } = task as Task;

  const [updateTaskMembers] = useUpdateTaskMembersMutation({
    refetchQueries: [
      'listActivitiesByTask',
      'listActivitiesByProject',
      'listActivities',
      'listProjects',
    ],
  });

  const [removeTaskMember] = useUpdateTaskMutation({
    refetchQueries: [
      'listActivitiesByTask',
      'listActivitiesByProject',
      'listActivities',
      'listProjects',
    ],
  });

  const [deleteTaskInvitation] = useDeleteTaskInvitationMutation({
    refetchQueries: [{ query: GetTaskDocument, variables: { id: taskId } }],
  });

  const [deleteUserRequest] = useDeleteUserRequestMutation({
    refetchQueries: [{ query: GetTaskDocument, variables: { id: taskId } }],
  });

  const [resendTaskInvitation] = useResendTaskInvitationMutation({
    refetchQueries: [{ query: GetTaskDocument, variables: { id: taskId } }],
  });

  const removeMemberFromTask = (
    memberId: string,
    callbacks: RemoveMemberCallbacks
  ) => {
    const users = members.filter((item) => item.user.id !== memberId);

    const newMembers = users.map((u) => {
      return {
        userId: u.user.id,
        role: u.role,
      };
    });

    const newMembersOptimistic = users.map((u) => {
      return {
        id: u.id,
        role: u.role,
        user: u.user,
      };
    });

    removeTaskMember({
      variables: {
        id,
        attributes: {
          name: name,
          projectId,
          members: newMembers,
        },
      },
      optimisticResponse: {
        updateTask: {
          ...(task as Task),
          members: newMembersOptimistic,
        },
      },
      onError: callbacks.onError,
      onCompleted: callbacks.onCompleted,
    });
  };

  const removeInviteMemberFromTask = (
    removeTaskMemberData: UserSearchType,
    callbacks: RemoveInviteCallbacks
  ) => {
    if (!removeTaskMemberData?.id) return;

    deleteTaskInvitation({
      variables: { taskInvitationId: removeTaskMemberData.id },
      optimisticResponse: {
        deleteTaskInvitation: true,
      },
      update(cache, { data }) {
        if (!data?.deleteTaskInvitation) {
          return;
        }
        removeInvitationFromCache(cache, 'Task', id, removeTaskMemberData.id!);
      },
      onCompleted: callbacks.onCompleted,
      onError: callbacks.onError,
    });
  };

  const removeUserRequestFromTask = (
    removeTaskMemberData: UserSearchType & { userRequestId?: string },
    callbacks: RemoveInviteCallbacks
  ) => {
    if (!removeTaskMemberData?.userRequestId) return;

    deleteUserRequest({
      variables: { id: removeTaskMemberData.userRequestId },
      update(cache, { data }) {
        if (!data?.deleteUserRequest) {
          return;
        }
        removeUserRequestFromCache(
          cache,
          'Task',
          id,
          removeTaskMemberData.userRequestId!
        );
      },
      onCompleted: callbacks.onCompleted,
      onError: callbacks.onError,
    });
  };

  const resendInviteMemberFromTask = (
    member: UserSearchType,
    callbacks: ResendInviteCallbacks
  ) => {
    if (!member?.id) return;
    resendTaskInvitation({
      variables: { taskInvitationId: member.id },
      onError: callbacks.onError,
      onCompleted: callbacks.onCompleted,
    });
  };

  const updateTaskMember = (
    userId: User['id'],
    role: TaskMemberRole,
    callbacks: UpdateRoleCallbacks
  ) => {
    updateTaskMembers({
      variables: {
        id,
        attributes: [
          {
            userId,
            role: getTaskRoleEnumValue(role),
          },
        ],
      },
      onError: callbacks.onError,
      onCompleted: callbacks.onCompleted,
    });
  };

  return {
    removeMemberFromTask,
    removeInviteMemberFromTask,
    removeUserRequestFromTask,
    resendInviteMemberFromTask,
    updateTaskMember,
  };
};
